import { fetchAuthSession } from "aws-amplify/auth";
import type {
  AssistantRequest,
  AssistantStreamEvent
} from "catchment-common/interfaces/llmQuery";

import { LLM_API_URL } from "@app/bootstrap/init";

const ASSISTANT_REQUEST_TIMEOUT_MS = 110_000;

const createRequestSignal = (
  signal?: AbortSignal
): { cleanup: () => void; signal: AbortSignal } => {
  const abortSignal = AbortSignal as typeof AbortSignal & {
    any?: (signals: AbortSignal[]) => AbortSignal;
    timeout?: (milliseconds: number) => AbortSignal;
  };

  if (abortSignal.timeout && abortSignal.any) {
    const timeoutSignal = abortSignal.timeout(ASSISTANT_REQUEST_TIMEOUT_MS);

    if (!signal) {
      return { cleanup: () => undefined, signal: timeoutSignal };
    }

    return {
      cleanup: () => undefined,
      signal: abortSignal.any([signal, timeoutSignal])
    };
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    ASSISTANT_REQUEST_TIMEOUT_MS
  );
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });

  return {
    cleanup: () => {
      window.clearTimeout(timeoutId);
      signal?.removeEventListener("abort", abort);
    },
    signal: controller.signal
  };
};

const getAuthToken = async (forceRefresh = false): Promise<string> => {
  const session = await fetchAuthSession({ forceRefresh });
  const token =
    session.tokens?.accessToken?.toString() ||
    session.tokens?.idToken?.toString();

  if (!token) {
    throw new Error("You must be signed in to use the assistant.");
  }

  return token;
};

const parseNdjsonChunk = (
  buffer: string
): { events: AssistantStreamEvent[]; remainder: string } => {
  const lines = buffer.split("\n");
  const remainder = lines.pop() ?? "";
  const events = lines
    .filter((line) => line.trim())
    .map((line) => JSON.parse(line) as AssistantStreamEvent);

  return { events, remainder };
};

async function* readLlmStream(
  request: AssistantRequest,
  token: string,
  signal?: AbortSignal
): AsyncGenerator<AssistantStreamEvent> {
  const requestSignal = createRequestSignal(signal);

  try {
    const response = await fetch(LLM_API_URL, {
      body: JSON.stringify(request),
      headers: {
        Accept: "application/x-ndjson",
        Authorization: token,
        "Content-Type": "application/json"
      },
      method: "POST",
      signal: requestSignal.signal
    });

    if (!response.ok) {
      const error = new Error(
        `Assistant request failed (${response.status}).`
      ) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    if (!response.body) {
      throw new Error("Assistant response did not include a stream body.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const parsed = parseNdjsonChunk(buffer);
      buffer = parsed.remainder;

      for (const event of parsed.events) {
        yield event;
      }
    }

    if (buffer.trim()) {
      yield JSON.parse(buffer) as AssistantStreamEvent;
    }
  } finally {
    requestSignal.cleanup();
  }
}

export async function* streamLlmQuery(
  request: AssistantRequest,
  signal?: AbortSignal
): AsyncGenerator<AssistantStreamEvent> {
  if (!LLM_API_URL) {
    throw new Error(
      "LLM API URL is not configured. Set VITE_LLM_API_URL after deploying LlmStack."
    );
  }

  try {
    yield* readLlmStream(request, await getAuthToken(), signal);
  } catch (error) {
    const statusCode = (error as { status?: number }).status;
    if (statusCode !== 401) {
      throw error;
    }

    yield* readLlmStream(request, await getAuthToken(true), signal);
  }
}
